import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}`);
        const paid = data.payment_status === "paid" || data.payment_status === "no_payment_required";
        const complete = data.status === "complete";
        // Success when either the backend confirms the subscription is active for this user,
        // OR Stripe says checkout is complete/paid (subscription_id may lag for a few seconds on trials).
        if (data.is_active || ((paid || complete) && data.subscription_id)) {
          await refreshUser();
          if (!cancelled) setStatus("success");
          return;
        }
      } catch {
        // keep retrying
      }
      if (cancelled) return;
      if (attempts >= 15) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="max-w-md w-full text-center px-6" data-testid="billing-success-pane">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-zinc-700 mb-6" />
            <h1 className="font-heading text-3xl tracking-tight text-zinc-900">Confirming your subscription…</h1>
            <p className="text-zinc-600 mt-3">This usually takes a few seconds.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-6" />
            <h1 className="font-heading text-3xl tracking-tight text-zinc-900">You're in. Welcome aboard.</h1>
            <p className="text-zinc-600 mt-3">Your 7-day free trial has started. We'll email you before the first charge.</p>
            <Button className="mt-8 h-12 px-8" onClick={() => nav("/app")} data-testid="goto-app-button">
              Go to translator
            </Button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-amber-600 mb-6" />
            <h1 className="font-heading text-3xl tracking-tight text-zinc-900">Hmm, we couldn't confirm yet.</h1>
            <p className="text-zinc-600 mt-3">If you completed payment, please refresh in a moment. Otherwise, head back to billing to retry.</p>
            <div className="flex gap-3 justify-center mt-8">
              <Button variant="outline" onClick={() => nav("/billing")} data-testid="back-to-billing-button">
                Back to billing
              </Button>
              <Button onClick={() => window.location.reload()} data-testid="retry-confirm-button">Retry</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
